import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { BlankSessionService } from '@helios/sessions'
import { RootState, selectSelectedReconDate } from '@helios/state-management'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { DisplayDateFormat } from '@northfork/basics'
import { ToastService, ToggleStates } from '@northfork/ng-basics'
import { Observable, Subject, forkJoin, of } from 'rxjs'
import { catchError, map, take, takeUntil } from 'rxjs/operators'
import {
  Break,
  BreakCodeResponse,
  BreakNoteHandling,
  BreakResponseObject,
} from '../../model/break.model'
import { MultiBreaksService } from '../../services/multi-breaks.service'
import { resolvePaste } from '../../model/break-code-paste'
import { isPasteBreakCodesEvent } from '../../utils/paste-break-codes-event'
import { BreakCodeClipboardService } from '../../services/break-code-clipboard.service'
import {
  BulkBreakGroup,
  buildBreakGroups,
  keepWouldDropCode,
} from '../../model/bulk-break-group.model'
import {
  actionCodeOptions,
  descriptionCodeOptions,
  notesRequiredActionCodes,
} from '../../validators/break-code-validation'
import {
  bulkDialogValidation,
  groupsToSaveDescriptors,
  aggregateResponses,
  EMPTY_SELECTION,
  GroupSelection,
  hasWriteTargetFor,
  isRestrictedForMode,
  KeepMode,
  minHoldLimitInDays,
  prefillGroupSelection,
  SaveDescriptor,
} from '../../validators/bulk-break-validation'
import { BulkBreakGroupComponent } from '../bulk-break-group/bulk-break-group.component'
import { BREAK_DIALOG_SHARED_STYLES } from '../break-dialog-shared.styles'
import {
  firstHoldTimeErrorMessage,
  HoldExpirationDateValidator,
  HoldExpirationDaysValidator,
  HoldModalValidationMessages,
} from '../../validators/hold-validators'

@Component({
  selector: 'hs-bulk-break-modal',
  templateUrl: './bulk-break-modal.component.html',
  styles: [
    BREAK_DIALOG_SHARED_STYLES,
    `
      /* nf-form-control carries a default bottom margin on its host. Drop it
         everywhere in this dialog so the vertical rhythm is driven by the
         explicit 12px gaps below (expires row, keep toggle, quick-apply box) —
         all matching the quick-apply-to-header gap. */
      nf-form-control {
        margin-bottom: 0;
      }
      .expires-row {
        display: flex;
        gap: 40px;
        align-items: flex-start;
        margin-bottom: 4px;
      }
      .expires-row .expires-value {
        flex: 1;
        min-width: 0;
      }
      /* Match the 12px middle tier for the days input and the date picker. */
      .expires-value ::ng-deep input {
        font-size: 12px;
      }
      /* Left "Expires" column: header + toggle, so it aligns with the field
         column to its right. */
      .expires-col {
        display: flex;
        flex-direction: column;
      }
      .expires-heading {
        height: 17px;
        margin-bottom: 0;
        display: flex;
        align-items: flex-end;
      }
      .control-label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #6b7a8d;
        margin-bottom: 2px;
      }
      .toggle-row {
        display: flex;
        gap: 40px;
        margin-bottom: 10px;
      }
      .segment-toggle {
        display: inline-flex;
        align-items: center;
        font-size: 12px;
      }
      .groups-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .save-disabled-reason {
        font-size: 12px;
        color: #db1f30;
        margin-right: auto;
      }
      .quick-apply {
        border: 1px solid #d4dae3;
        background: #f7f8fa;
        border-radius: 4px;
        padding: 8px 12px;
        margin-bottom: 12px;
      }
      /* Match the expanded group's middle font tier (12px) for the
         Quick Apply code dropdowns and the Quick Apply note box. */
      .quick-apply ::ng-deep .ng-select,
      .quick-apply ::ng-deep .ng-dropdown-panel .ng-option,
      .quick-apply textarea.form-control {
        font-size: 12px;
      }
      .quick-apply-feedback {
        font-size: 12px;
        color: #6b7a8d;
        margin-top: 4px;
      }
      .quick-apply-empty {
        font-size: 12px;
        font-style: italic;
        color: #6b7a8d;
      }
      .quick-apply-title {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #6b7a8d;
        margin-bottom: 7px;
      }
      .section-label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #6b7a8d;
        margin-bottom: 8px;
      }
    `,
  ],
})
export class BulkBreakModalComponent implements OnDestroy {
  private _mode: 'hold' | 'accept' = 'hold'
  @Input() set mode(value: 'hold' | 'accept') {
    this._mode = value
    this.updateLabels()
  }
  get mode(): 'hold' | 'accept' {
    return this._mode
  }

  private _breaks: Break[] = []
  private _breakCodes: BreakCodeResponse | undefined

  @Input() set breaks(value: Break[]) {
    if (value.length === 0) {
      this.activeModal.dismiss()
      return
    }
    this._breaks = value
    this.rebuildGroups()
    this.updateLabels()
    this.applyHoldLimit()
  }
  get breaks(): Break[] {
    return this._breaks
  }

  @Input() set breakCodes(value: BreakCodeResponse | undefined) {
    this._breakCodes = value
    this.rebuildGroups()
  }

  @Input() set days(value: number | undefined) {
    if (value == undefined) return
    this.expiresInDays.setValue(value)
  }

  @ViewChildren(BulkBreakGroupComponent)
  groupComponents!: QueryList<BulkBreakGroupComponent>

  groups: BulkBreakGroup[] = []
  selections = new Map<string, GroupSelection>()
  expandedGroups = new Set<string>()

  /** Per-group save lifecycle, keyed by breakType; bound into the child via [saveState]. */
  readonly groupSaveStates = new Map<
    string,
    'idle' | 'saving' | 'saved' | 'failed'
  >()
  /** Responses from groups that have succeeded across this + prior attempts. */
  private readonly succeededResponses = new Map<string, BreakResponseObject>()

  quickApplyAction = new FormControl(null) // untyped (Angular 12)
  quickApplyDescription = new FormControl(null)
  quickApplyNote = new FormControl('')
  quickApplyFeedback = ''
  // Cached so the template binds a STABLE array reference. Re-evaluating a
  // method that returns a fresh array in ng-select's [items] every change-
  // detection cycle breaks option selection. Recomputed in rebuildGroups().
  quickApplyActionItems: string[] = []
  quickApplyDescriptionItems: string[] = []
  // Union of action codes (across all groups) whose selection requires a note,
  // so the quick-apply Action dropdown shows the same ◆ as the per-group selects.
  quickApplyNotesRequiredSet = new Set<string>()
  // Last code broadcast per field, so clearing the bar reverts only the groups
  // that still hold it.
  private lastQuickApplyAction: string | null = null
  private lastQuickApplyDescription: string | null = null
  // Last note broadcast, so clearing/retyping the box reverts only the groups
  // that still hold it (a per-group note the user edited is left untouched).
  private lastQuickApplyNote: string | null = null

  // Untyped (Angular 12). Value is `'keep' | 'override'` by construction.
  keepControl = new FormControl('keep')
  get keepMode(): KeepMode {
    return this.keepControl.value as KeepMode
  }

  // Bulk-only default (PVAL-26487): append so a bulk hold/resolve builds on
  // historic notes. The single-break and Edit Note modals keep Override.
  noteHandlingControl = new FormControl('append')
  get appendNotes(): boolean {
    return this.noteHandlingControl.value === 'append'
  }
  get resolvedNoteHandling(): BreakNoteHandling {
    return this.appendNotes
      ? BreakNoteHandling.APPEND
      : BreakNoteHandling.REPLACE
  }

  // Hold-only — mirrors HoldModalComponent.
  private readonly currentDate = new Date()
  expiresInDays = new FormControl(1, [Validators.required])
  // false = expires in days, true = expire on date.
  expiresToggle = new FormControl(false, [])
  expiresOnDate = new FormControl(new Date(), [])

  form = new FormGroup({
    keep: this.keepControl,
    noteHandling: this.noteHandlingControl,
    expiresInDays: this.expiresInDays,
    expiresOnDate: this.expiresOnDate,
    expiresToggle: this.expiresToggle,
    quickApplyAction: this.quickApplyAction,
    quickApplyDescription: this.quickApplyDescription,
    quickApplyNote: this.quickApplyNote,
  })

  expiresToggleStates: ToggleStates = {
    on: 'Specific Date',
    off: 'Expire in Days',
  }
  dateFormat = DisplayDateFormat
  validationMessages = HoldModalValidationMessages

  modalTitle = 'Hold Breaks'
  saveBtnLabel = 'Save'
  saving = false

  private readonly destroy$ = new Subject<void>()

  constructor(
    public activeModal: NgbActiveModal,
    private readonly breaksService: MultiBreaksService,
    private readonly blankSessionService: BlankSessionService,
    public toastService: ToastService,
    private readonly store: Store<RootState>,
    private readonly breakCodeClipboard: BreakCodeClipboardService,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    this.expiresToggle.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(onDate => this.onExpiresToggleChange(!!onDate))
    // Broadcast the quick-apply note to the groups live (per keystroke) so Save
    // enables/disables immediately, rather than only on blur.
    this.quickApplyNote.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(note => this.applyQuickNote(note))
    this.noteHandlingControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.swapPrefilledNotes())
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  get validation() {
    return bulkDialogValidation(this.groups, this.selections, this.keepMode)
  }

  get isHold(): boolean {
    return this._mode === 'hold'
  }

  /** Any selected break needs approval — drives the restricted-action notice. */
  get hasRestrictedBreaks(): boolean {
    return this.groups.some(g =>
      g.breaks.some(b => isRestrictedForMode(b, this.mode)),
    )
  }

  /**
   * How many selected breaks will silently NOT receive the chosen codes — see
   * keepWouldDropCode for the per-field Keep semantics. Counted per break, not per
   * group, because that is what gets skipped and because the existing signals are weak:
   * "Applied to N groups" counts a group with a SINGLE uncoded break as applied, and
   * groups are collapsed by default.
   */
  get keepBlockedCodeCount(): number {
    if (this.keepMode !== 'keep') return 0
    return this.groups.reduce((skipped, group) => {
      const selection = this.selections.get(group.breakType)
      const dropped = group.breaks.filter(b =>
        keepWouldDropCode(
          b,
          selection?.actionCode ?? null,
          selection?.descriptionCode ?? null,
        ),
      ).length
      return skipped + dropped
    }, 0)
  }

  /** Whether the non-blocking save caution is showing under the toggle row. */
  get hasCautions(): boolean {
    return this.keepBlockedCodeCount > 0
  }

  /** Message for an invalid expiry control (hold only), or null when valid. */
  get expiresError(): string | null {
    if (!this.isHold) return null
    for (const ctrl of [this.expiresInDays, this.expiresOnDate]) {
      if (ctrl.enabled && ctrl.invalid && ctrl.errors) {
        return firstHoldTimeErrorMessage(ctrl.errors)
      }
    }
    return null
  }

  /**
   * Why Save is disabled, for the footer reason + tooltip: a per-group blocker
   * if any, else the expiry error (which form.invalid alone wouldn't surface).
   */
  get saveDisabledReason(): string {
    if (this.validation.submitDisabled) {
      return this.validation.reasons[0] ?? ''
    }
    return this.expiresError ?? ''
  }

  /** True once at least one group has failed a save attempt (dialog stays open for retry). */
  get hasFailures(): boolean {
    return this.pendingCount > 0
  }

  /** Count of groups still needing a (re)save: failed on a prior attempt, or never attempted. */
  get pendingCount(): number {
    return [...this.groupSaveStates.values()].filter(s => s === 'failed').length
  }

  /** Submit button label: 'Retry failed (K)' after a partial failure, else the standard label. */
  get submitBtnLabel(): string {
    return this.hasFailures
      ? `Retry failed (${this.pendingCount})`
      : this.saveBtnLabel
  }

  isExpanded(breakType: string): boolean {
    return this.expandedGroups.has(breakType)
  }

  onGroupSelectionChange(breakType: string, sel: GroupSelection): void {
    this.selections.set(breakType, sel)
    // If the user edits a group that already saved (dialog still open after a
    // partial failure), mark it for re-save and drop its now-stale response.
    if (this.groupSaveStates.get(breakType) === 'saved') {
      this.groupSaveStates.set(breakType, 'idle')
      this.succeededResponses.delete(breakType)
    }
  }

  onToggleGroup(breakType: string): void {
    if (this.expandedGroups.has(breakType)) {
      this.expandedGroups.delete(breakType)
    } else {
      this.expandedGroups.add(breakType)
    }
  }

  /** Cached action codes common to every current group. */
  quickApplyActionOptions(): string[] {
    return this.quickApplyActionItems
  }

  /** Cached description codes common to every current group. */
  quickApplyDescriptionOptions(): string[] {
    return this.quickApplyDescriptionItems
  }

  /** Codes valid for EVERY current group — the intersection across selected types. */
  private commonCodes(pick: (g: BulkBreakGroup) => string[]): string[] {
    if (this.groups.length === 0) return []
    const [first, ...rest] = this.groups
    return pick(first).filter(code => rest.every(g => pick(g).includes(code)))
  }

  applyQuickAction(code: string | null): void {
    // Revert the previous quick-apply first so a cleared/replaced code doesn't
    // leave a stale value behind on the groups.
    this.clearQuick('action')
    if (!code) return
    this.applyQuick(code, 'action')
    this.lastQuickApplyAction = code
  }

  applyQuickDescription(code: string | null): void {
    this.clearQuick('description')
    if (!code) return
    this.applyQuick(code, 'description')
    this.lastQuickApplyDescription = code
  }

  // Quick-apply only offers codes common to EVERY group, so the chosen code is
  // always valid for all of them — write it to each.
  private applyQuick(code: string, field: 'action' | 'description'): void {
    let applied = 0
    for (const g of this.groups) {
      this.setGroupField(g, field, code)
      // Keep mode with no write target for THIS field (every break already coded
      // on it) writes nothing — don't count it as applied.
      if (hasWriteTargetFor(g, this.keepMode, field)) {
        applied++
      }
    }
    this.quickApplyFeedback = `"${code}" Applied to ${applied} groups.`
  }

  /** Revert the groups still holding the last quick-applied code for this field. */
  private clearQuick(field: 'action' | 'description'): void {
    const last =
      field === 'action'
        ? this.lastQuickApplyAction
        : this.lastQuickApplyDescription
    if (last != null) {
      for (const g of this.groups) {
        if (this.currentGroupValue(g, field) === last) {
          this.setGroupField(g, field, null)
        }
      }
    }
    if (field === 'action') this.lastQuickApplyAction = null
    else this.lastQuickApplyDescription = null
    this.quickApplyFeedback = ''
  }

  /**
   * Broadcast the quick-apply note to every group, live per keystroke — mirrors
   * applyQuickAction: revert the groups still holding the previous value, then write
   * the new one to all groups. Filling or changing the box replaces every group's
   * note (a manually-entered per-group note stands until the box is changed again).
   * Clearing it (empty) reverts the groups the box still owns, leaving a note the
   * user typed into a group by hand (no longer matching the last broadcast) untouched.
   */
  applyQuickNote(note: string | null): void {
    const value = note ?? ''
    this.revertQuickNote()
    if (!value) return
    for (const g of this.groups) {
      this.setGroupNote(g, value)
    }
    this.lastQuickApplyNote = value
    this.quickApplyFeedback = `Note applied to ${this.groups.length} groups.`
  }

  get canPaste(): boolean {
    return this.breakCodeClipboard.hasContent
  }

  /**
   * Fill the Quick Apply boxes from the in-app clipboard.
   *
   * The groups are written EXPLICITLY because ng-select's (change) output — how the
   * code controls reach the per-type groups — does NOT fire for a programmatic
   * setValue; a bare setValue would leave the dialog looking filled while every group
   * still held nothing. A CLEARING paste must bypass the quick-apply revert
   * bookkeeping — see pasteCodeToGroups.
   */
  onPasteBreakCodes(): void {
    const entry = this.breakCodeClipboard.read()
    if (!entry) {
      return
    }

    const result = resolvePaste(
      entry,
      this.quickApplyActionItems,
      this.quickApplyDescriptionItems,
    )

    this.quickApplyAction.setValue(result.actionCode)
    this.pasteCodeToGroups('action', result.actionCode)
    this.quickApplyDescription.setValue(result.descriptionCode)
    this.pasteCodeToGroups('description', result.descriptionCode)

    // setValue fires the constructor's quickApplyNote.valueChanges synchronously, so a
    // NON-EMPTY note has already reached every group. An empty one has not
    // (revertQuickNote only touches groups still holding lastQuickApplyNote), so clear
    // them outright — AFTER the setValue, so the subscription cannot undo it.
    this.quickApplyNote.setValue(result.note)
    if (!result.note) {
      for (const g of this.groups) {
        this.setGroupNote(g, '')
      }
    }

    if (result.error) {
      this.toastService.error(result.error)
    }
  }

  /**
   * Write a pasted code to EVERY group, including a null one — never skip the null
   * case. A non-null code takes the ordinary quick-apply path (records lastQuickApply*,
   * sets feedback); a null one must NOT, because applyQuick*'s clear step reverts only
   * groups holding lastQuickApply*, which is null on a fresh dialog whose groups are
   * already seeded by BulkBreakGroupComponent.prefillFromBreaks. Routing the
   * strict-mirror clear through that bookkeeping would blank the Quick Apply boxes and
   * change nothing underneath, so Save would write codes the user believes they cleared.
   */
  private pasteCodeToGroups(
    field: 'action' | 'description',
    code: string | null,
  ): void {
    if (code) {
      if (field === 'action') this.applyQuickAction(code)
      else this.applyQuickDescription(code)
      return
    }
    for (const g of this.groups) {
      this.setGroupField(g, field, null)
    }
    if (field === 'action') this.lastQuickApplyAction = null
    else this.lastQuickApplyDescription = null
    this.quickApplyFeedback = ''
  }

  /** alt+v — see isPasteBreakCodesEvent for why this is not a hotkeys-registry binding. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!isPasteBreakCodesEvent(event, this.host.nativeElement)) {
      return
    }
    event.preventDefault()
    this.onPasteBreakCodes()
  }

  /** Revert the groups still holding the last quick-applied note back to empty. */
  private revertQuickNote(): void {
    if (this.lastQuickApplyNote != null) {
      for (const g of this.groups) {
        if (this.currentGroupNote(g) === this.lastQuickApplyNote) {
          this.setGroupNote(g, '')
        }
      }
    }
    this.lastQuickApplyNote = null
    this.quickApplyFeedback = ''
  }

  /**
   * Move a group's shared existing note into the box under Override and out of it
   * under Append (why: see prefillGroupSelection). Out means off the front, where the
   * note ends at a word boundary: text the user added after it stays. An empty box is
   * the only one filled on Override; any other text is left as typed. A group that
   * already saved is skipped: writing its note re-queues it (onGroupSelectionChange),
   * and Retry would send it again. Done here, in the toggle's event path, not in the
   * child's ngOnChanges, whose selectionChange emit would land in the NG0100 window
   * the seeding in rebuildGroups() guards against.
   */
  private swapPrefilledNotes(): void {
    for (const g of this.groups) {
      if (this.groupSaveStates.get(g.breakType) === 'saved') continue
      const prefill = prefillGroupSelection(g).note
      if (!prefill) continue
      const current = this.currentGroupNote(g)
      if (!this.appendNotes) {
        if (current === '') this.setGroupNote(g, prefill)
        continue
      }
      const rest = current.slice(prefill.length)
      if (current.startsWith(prefill) && (!rest || /^\s/.test(rest))) {
        this.setGroupNote(g, rest.trimStart())
      }
    }
  }

  /** A group's current note, from the live child or pending selection. */
  private currentGroupNote(g: BulkBreakGroup): string {
    const child = this.childFor(g)
    if (child) {
      return child.form.controls.note.value ?? ''
    }
    return this.selections.get(g.breakType)?.note ?? ''
  }

  /** Set a group's note on the live child form, else on selections. */
  private setGroupNote(g: BulkBreakGroup, note: string): void {
    const child = this.childFor(g)
    if (child) {
      child.form.controls.note.setValue(note)
      return
    }
    const prev = this.selections.get(g.breakType) ?? EMPTY_SELECTION
    this.selections.set(g.breakType, { ...prev, note })
  }

  /** The live child component for a group, if it's rendered. */
  private childFor(g: BulkBreakGroup): BulkBreakGroupComponent | undefined {
    return this.groupComponents?.find(c => c.group.breakType === g.breakType)
  }

  /** A group's current action/description, from the live child or selections. */
  private currentGroupValue(
    g: BulkBreakGroup,
    field: 'action' | 'description',
  ): string | null {
    const child = this.childFor(g)
    if (child) {
      return child.form.controls[field].value ?? null
    }
    const sel = this.selections.get(g.breakType)
    return (field === 'action' ? sel?.actionCode : sel?.descriptionCode) ?? null
  }

  /** Set a group's action/description on the live child form, else on selections. */
  private setGroupField(
    g: BulkBreakGroup,
    field: 'action' | 'description',
    value: string | null,
  ): void {
    const child = this.childFor(g)
    if (child) {
      // Updating the child form re-emits selectionChange → keeps selections in sync.
      child.form.controls[field].setValue(value)
      return
    }
    const prev = this.selections.get(g.breakType) ?? EMPTY_SELECTION
    this.selections.set(g.breakType, {
      ...prev,
      actionCode: field === 'action' ? value : prev.actionCode,
      descriptionCode: field === 'description' ? value : prev.descriptionCode,
    })
  }

  onCancel(): void {
    this.activeModal.dismiss()
  }

  private buildGroupCall(
    d: SaveDescriptor,
    reconDate: Date | undefined,
  ): Observable<BreakResponseObject> {
    if (this.mode === 'accept') {
      return this.breaksService.accept(
        d.breakIds,
        d.note,
        this.resolvedNoteHandling,
        d.restrictedBreakIds,
        d.actionCode,
        d.descriptionCode,
        d.preserveExistingBreakCodes,
      )
    }
    if (this.expiresToggle.value) {
      return this.breaksService.holdToDate(
        d.breakIds,
        this.expiresOnDate.value,
        d.note,
        this.resolvedNoteHandling,
        d.restrictedBreakIds,
        d.actionCode,
        d.descriptionCode,
        d.preserveExistingBreakCodes,
      )
    }
    return this.breaksService.holdForDays(
      d.breakIds,
      this.expiresInDays.value,
      d.note,
      this.resolvedNoteHandling,
      d.restrictedBreakIds,
      reconDate,
      d.actionCode,
      d.descriptionCode,
      d.preserveExistingBreakCodes,
    )
  }

  async onSave(): Promise<void> {
    if (this.validation.submitDisabled || this.form.invalid || this.saving)
      return

    this.saving = true
    try {
      await this.blankSessionService.createBlankSessionIfNotInSession()
    } catch {
      this.saving = false
      this.toastService.error('Could not start a session — please retry.')
      return
    }

    const reconDate = await this.store
      .select(selectSelectedReconDate)
      .pipe(take(1))
      .toPromise()

    // Selective retry: skip groups that already succeeded on a prior attempt.
    const descriptors = groupsToSaveDescriptors(
      this.groups,
      this.selections,
      this.keepMode,
      this.mode,
    ).filter(d => this.groupSaveStates.get(d.breakType) !== 'saved')

    if (descriptors.length === 0) {
      this.saving = false
      return
    }

    descriptors.forEach(d => this.groupSaveStates.set(d.breakType, 'saving'))

    const settled = await forkJoin(
      descriptors.map(d =>
        this.buildGroupCall(d, reconDate).pipe(
          take(1),
          map(response => ({ id: d.breakType, ok: true, response } as const)),
          catchError(() => of({ id: d.breakType, ok: false } as const)),
        ),
      ),
    ).toPromise()

    const results = settled ?? []
    results.forEach(r => {
      if (r.ok) {
        this.groupSaveStates.set(r.id, 'saved')
        this.succeededResponses.set(r.id, r.response)
      } else {
        this.groupSaveStates.set(r.id, 'failed')
      }
    })

    this.saving = false

    const failedCount = results.filter(r => !r.ok).length
    if (failedCount > 0) {
      this.toastService.error(
        `${failedCount} of ${descriptors.length} groups failed — retry`,
      )
      return // keep the dialog open for retry
    }

    this.activeModal.close(
      aggregateResponses([...this.succeededResponses.values()]),
    )
  }

  private onExpiresToggleChange(onDate: boolean): void {
    if (onDate) {
      this.expiresOnDate.enable()
      this.expiresInDays.disable()
      return
    }
    this.expiresOnDate.disable()
    this.expiresInDays.enable()
  }

  private rebuildGroups(): void {
    this.groups = buildBreakGroups(this._breaks, this._breakCodes)
    this.quickApplyActionItems = this.commonCodes(g =>
      actionCodeOptions(g.availability),
    )
    this.quickApplyDescriptionItems = this.commonCodes(g =>
      descriptionCodeOptions(g.availability),
    )
    const noteSet = new Set<string>()
    for (const g of this.groups) {
      notesRequiredActionCodes(g.availability).forEach(c => noteSet.add(c))
    }
    this.quickApplyNotesRequiredSet = noteSet
    const types = new Set(this.groups.map(g => g.breakType))
    for (const key of Array.from(this.selections.keys())) {
      if (!types.has(key)) this.selections.delete(key)
    }
    // Seed a new group with the same prefill its child form will show. Without this
    // the child's initial emit changes validation after the dialog's own template
    // pass has read it (NG0100). Never overwrite an existing entry — that's user input.
    for (const g of this.groups) {
      if (!this.selections.has(g.breakType)) {
        this.selections.set(
          g.breakType,
          prefillGroupSelection(g, !this.appendNotes),
        )
      }
    }
  }

  private updateLabels(): void {
    const n = this._breaks.length
    const verb = this._mode === 'accept' ? 'Accept' : 'Hold'
    this.modalTitle = n > 0 ? `${verb} Breaks (${n})` : `${verb} Breaks`
    this.saveBtnLabel = n > 0 ? `Save (${n})` : 'Save'
  }

  private applyHoldLimit(): void {
    if (!this.isHold) return // hold-only; never gate Accept on hold limits
    const limit = minHoldLimitInDays(this._breaks)
    if (limit == undefined) return
    const dateLimit = new Date(this.currentDate)
    dateLimit.setDate(dateLimit.getDate() + limit)
    this.expiresInDays.setValidators([
      Validators.required,
      HoldExpirationDaysValidator(limit),
    ])
    this.expiresOnDate.setValidators([
      Validators.required,
      HoldExpirationDateValidator(this.currentDate, dateLimit),
    ])
    this.expiresInDays.updateValueAndValidity()
    this.expiresOnDate.updateValueAndValidity()
  }
}
